import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import {
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  FileText,
  Loader2,
  Newspaper,
  Send,
  Share2,
  WifiOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  MarketingFilterPanel,
  MarketingFilterSearch,
  MarketingFilterSelect,
  marketingFilterSelectClass,
  marketingMatchSummary,
} from './components/MarketingListFilters';
import { ConnectionCallout, InsightTile } from './components/InsightTile';
import { EmptyState } from './components/marketing-ui';
import { useTabSearchParam } from '@/client/hooks/useTabSearchParam';
import { useCalendarQuery, useRescheduleMutation } from '@/client/api/marketing/studio';
import { usePlaysQuery } from '@/client/api/marketing/plays';
import { getApiErrorMessage } from '@/client/utils/errors';

type ScheduleKind = 'content' | 'social' | 'traditional';
type ScheduleSort = 'soonest' | 'latest' | 'title' | 'kind';
type TimeWindow = 'upcoming' | 'past' | 'all';
type CalendarView = 'day' | 'week' | 'month' | 'agenda';

type ScheduleEntry = {
  /** `kind:pk` — unique across the three kinds, which share an id space. */
  id: string;
  /** The primary key on its own table, for the reschedule call. */
  pk: number;
  kind: ScheduleKind;
  title: string;
  date: string;
  route: string;
  meta: string;
  playId?: number;
  playLabel?: string;
};

const KIND_ICON = { content: FileText, social: Share2, traditional: Newspaper } as const;
const KIND_LABEL = {
  content: 'Content',
  social: 'Social queue',
  traditional: 'Press queue',
} as const;
const KIND_TONE = {
  content: 'bg-blue-50 text-blue-700',
  social: 'bg-fuchsia-50 text-fuchsia-700',
  traditional: 'bg-violet-50 text-violet-700',
} as const;

const TABS = ['list', 'calendar'] as const;
const KIND_FILTERS: (ScheduleKind | 'all')[] = ['all', 'content', 'social', 'traditional'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CAL_VIEWS: { id: CalendarView; label: string }[] = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'agenda', label: 'Agenda' },
];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function dayKey(iso: string) {
  return iso.slice(0, 10);
}

function utcDateOnly(d: Date) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function addUtcDays(d: Date, days: number) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days));
}

function startOfUtcWeek(d: Date) {
  return addUtcDays(utcDateOnly(d), -d.getUTCDay());
}

function formatDayLabel(d: Date, opts?: Intl.DateTimeFormatOptions) {
  return d.toLocaleDateString(undefined, { timeZone: 'UTC', ...opts });
}

function hourLabel(h: number) {
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr} ${ampm}`;
}

/**
 * Where a dragged entry lands.
 *
 * The time of day is preserved unless a specific hour was dropped on, so moving
 * a post from Tuesday to Thursday in month view does not silently reset a
 * carefully chosen 09:15 to midnight.
 */
function nextSlot(entry: ScheduleEntry, newDay: string, hour?: number): string {
  const original = new Date(entry.date);
  const next = new Date(`${newDay}T00:00:00Z`);
  if (typeof hour === 'number') {
    next.setUTCHours(hour, original.getUTCMinutes(), original.getUTCSeconds());
  } else {
    next.setUTCHours(original.getUTCHours(), original.getUTCMinutes(), original.getUTCSeconds());
  }
  return next.toISOString();
}

function DraggableEntryChip({
  entry,
  onOpen,
  dense,
}: {
  entry: ScheduleEntry;
  onOpen: (entry: ScheduleEntry) => void;
  dense?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${entry.kind}_${entry.id}`,
    data: entry,
  });
  return (
    <button
      type="button"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onDoubleClick={() => onOpen(entry)}
      title={`${entry.title} — double-click to open`}
      className={cn(
        'block w-full cursor-grab truncate rounded px-1.5 text-left font-medium active:cursor-grabbing',
        dense ? 'py-0.5 text-[10px]' : 'py-1 text-[11px]',
        KIND_TONE[entry.kind],
        isDragging && 'opacity-40',
      )}
    >
      {entry.title}
    </button>
  );
}

function DroppableDayCell({
  day,
  children,
  className,
}: {
  day: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: day });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-lg border bg-white p-2 transition-colors',
        isOver ? 'border-blue-400 bg-blue-50' : 'border-gray-100',
        className,
      )}
    >
      {children}
    </div>
  );
}

function DroppableHourRow({
  day,
  hour,
  children,
}: {
  day: string;
  hour: number;
  children: React.ReactNode;
}) {
  const id = `${day}T${String(hour).padStart(2, '0')}`;
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn('min-h-[40px] px-1 py-0.5 transition-colors', isOver && 'bg-blue-50')}
    >
      {children}
    </div>
  );
}

const SchedulingPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useTabSearchParam(TABS, 'list');
  const [kindFilter, setKindFilter] = useState<ScheduleKind | 'all'>('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [windowFilter, setWindowFilter] = useState<TimeWindow>('all');
  const [sort, setSort] = useState<ScheduleSort>('soonest');
  const [search, setSearch] = useState('');
  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [focusDate, setFocusDate] = useState<Date | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  /**
   * One feed, not three.
   *
   * Content, posts and placements arrive already merged and sorted. Merging them
   * client-side is only correct while all three fit in one page — the moment any
   * of them paginates, a calendar built from three independent lists silently
   * drops whatever fell off the end of the shortest one.
   */
  const { data, isLoading, isError, error } = useCalendarQuery();
  const plays = usePlaysQuery({ limit: 100 });
  const reschedule = useRescheduleMutation();

  const playName = (id?: number | null) =>
    plays.data?.items.find((p) => p.play_id === id)?.name;

  const entries = useMemo<ScheduleEntry[]>(
    () =>
      (data?.items ?? [])
        .filter((item) => !!item.scheduled_at)
        .map((item) => ({
          id: `${item.kind}:${item.id}`,
          pk: item.id,
          kind: item.kind,
          title: item.title || '(untitled)',
          date: item.scheduled_at as string,
          route:
            item.kind === 'content'
              ? `/admin/marketing/studio/content/${item.id}`
              : item.kind === 'social'
                ? '/admin/marketing/studio/social'
                : '/admin/marketing/studio/traditional',
          meta: item.status,
          playId: item.play_id ?? undefined,
          playLabel: playName(item.play_id),
        })),
    [data, plays.data],
  );

  const resolvedFocus = useMemo(() => {
    if (focusDate) return utcDateOnly(focusDate);
    const now = Date.now();
    const upcoming = entries
      .filter((e) => new Date(e.date).getTime() >= now)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
    const anchor =
      upcoming ?? [...entries].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
    if (anchor) return utcDateOnly(new Date(anchor.date));
    return utcDateOnly(new Date());
  }, [focusDate, entries]);

  useEffect(() => {
    if (!focusDate && entries.length > 0) setFocusDate(resolvedFocus);
  }, [focusDate, entries.length, resolvedFocus]);

  const insights = useMemo(() => {
    const now = Date.now();
    const upcoming = entries.filter((e) => new Date(e.date).getTime() >= now).length;
    const past = entries.length - upcoming;
    const byKind = {
      content: entries.filter((e) => e.kind === 'content').length,
      social: entries.filter((e) => e.kind === 'social').length,
      traditional: entries.filter((e) => e.kind === 'traditional').length,
    };
    // The unified feed carries the play, not the content link, so this is
    // play attribution — the thing the calendar is actually used to check.
    const linked = entries.filter((e) => e.playId != null).length;
    const next = [...entries]
      .filter((e) => new Date(e.date).getTime() >= now)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
    return { upcoming, past, byKind, linked, next };
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const list = entries.filter((entry) => {
      if (kindFilter !== 'all' && entry.kind !== kindFilter) return false;
      if (campaignFilter === 'none' && entry.playId != null) return false;
      if (
        campaignFilter !== 'all' &&
        campaignFilter !== 'none' &&
        String(entry.playId) !== campaignFilter
      ) {
        return false;
      }
      const t = new Date(entry.date).getTime();
      if (windowFilter === 'upcoming' && t < now) return false;
      if (windowFilter === 'past' && t >= now) return false;
      if (q) {
        const hay = [entry.title, entry.meta, entry.playLabel ?? '', KIND_LABEL[entry.kind]]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    list.sort((a, b) => {
      switch (sort) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'kind':
          return a.kind.localeCompare(b.kind) || new Date(a.date).getTime() - new Date(b.date).getTime();
        case 'latest':
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case 'soonest':
        default:
          return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });
    return list;
  }, [entries, kindFilter, campaignFilter, windowFilter, search, sort]);

  const byDay = useMemo(() => {
    const map = new Map<string, ScheduleEntry[]>();
    for (const entry of filteredEntries) {
      const key = dayKey(entry.date);
      map.set(key, [...(map.get(key) ?? []), entry]);
    }
    return map;
  }, [filteredEntries]);

  const weekDays = useMemo(() => {
    const start = startOfUtcWeek(resolvedFocus);
    return Array.from({ length: 7 }, (_, i) => addUtcDays(start, i));
  }, [resolvedFocus]);

  const monthCells = useMemo(() => {
    const year = resolvedFocus.getUTCFullYear();
    const month = resolvedFocus.getUTCMonth();
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const leadingBlanks = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const days = Array.from({ length: daysInMonth }, (_, i) =>
      new Date(Date.UTC(year, month, i + 1)).toISOString().slice(0, 10),
    );
    return [...Array.from({ length: leadingBlanks }, () => null), ...days];
  }, [resolvedFocus]);

  const focusDayKey = resolvedFocus.toISOString().slice(0, 10);

  const rangeLabel = useMemo(() => {
    if (calendarView === 'day') {
      return formatDayLabel(resolvedFocus, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
    if (calendarView === 'week') {
      return `${formatDayLabel(weekDays[0], { month: 'short', day: 'numeric' })} – ${formatDayLabel(
        weekDays[6],
        { month: 'short', day: 'numeric', year: 'numeric' },
      )}`;
    }
    return formatDayLabel(resolvedFocus, { month: 'long', year: 'numeric' });
  }, [calendarView, resolvedFocus, weekDays]);

  const agendaEntries = useMemo(() => {
    const y = resolvedFocus.getUTCFullYear();
    const m = resolvedFocus.getUTCMonth();
    return filteredEntries.filter((e) => {
      const d = new Date(e.date);
      return d.getUTCFullYear() === y && d.getUTCMonth() === m;
    });
  }, [resolvedFocus, filteredEntries]);

  const openEntry = (entry: ScheduleEntry) => navigate(entry.route);

  const shiftFocus = (direction: -1 | 1) => {
    setFocusDate((prev) => {
      const base = prev ?? resolvedFocus;
      if (calendarView === 'day') return addUtcDays(base, direction);
      if (calendarView === 'week') return addUtcDays(base, direction * 7);
      return new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + direction, 1));
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const entry = active.data.current as ScheduleEntry | undefined;
    if (!entry) return;
    const overId = String(over.id);
    const hourMatch = overId.match(/^(\d{4}-\d{2}-\d{2})T(\d{2})$/);
    const newDay = hourMatch ? hourMatch[1] : overId;
    const hour = hourMatch ? Number(hourMatch[2]) : undefined;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDay)) return;
    if (dayKey(entry.date) === newDay && hour === undefined) return;
    if (dayKey(entry.date) === newDay && hour !== undefined && new Date(entry.date).getUTCHours() === hour) {
      return;
    }
    reschedule.mutate(
      { kind: entry.kind, id: entry.pk, scheduled_at: nextSlot(entry, newDay, hour) },
      {
        onSuccess: () =>
          toast.success(
            hour !== undefined
              ? `Rescheduled "${entry.title}" to ${newDay} ${hourLabel(hour)}`
              : `Rescheduled "${entry.title}" to ${newDay}`,
          ),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not reschedule')),
      },
    );
  };

  const clearFilters = () => {
    setKindFilter('all');
    setCampaignFilter('all');
    setWindowFilter('all');
    setSearch('');
    setSort('soonest');
  };

  const kindFilterOptions = useMemo(
    () =>
      KIND_FILTERS.map((k) => ({
        value: k,
        label: k === 'all' ? 'All kinds' : KIND_LABEL[k],
      })),
    [],
  );

  const campaignFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All plays' },
      { value: 'none', label: 'No play' },
      ...(plays.data?.items ?? []).map((p) => ({
        value: String(p.play_id),
        label: p.name,
      })),
    ],
    [plays.data?.items],
  );

  const windowFilterOptions = useMemo(
    () => [
      { value: 'upcoming', label: 'Upcoming' },
      { value: 'past', label: 'Past' },
      { value: 'all', label: 'All time' },
    ],
    [],
  );

  const sortOptions = useMemo(
    () => [
      { value: 'soonest', label: 'Sort: Soonest' },
      { value: 'latest', label: 'Sort: Latest' },
      { value: 'title', label: 'Sort: Title' },
      { value: 'kind', label: 'Sort: Kind' },
    ],
    [],
  );

  const filtersActive =
    kindFilter !== 'all' ||
    campaignFilter !== 'all' ||
    windowFilter !== 'all' ||
    search.trim().length > 0 ||
    sort !== 'soonest';

  const renderHourGrid = (day: string) => {
    const dayEntries = byDay.get(day) ?? [];
    const byHour = new Map<number, ScheduleEntry[]>();
    for (const entry of dayEntries) {
      const h = new Date(entry.date).getUTCHours();
      byHour.set(h, [...(byHour.get(h) ?? []), entry]);
    }
    return (
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {HOURS.map((h) => (
          <div key={h} className="grid grid-cols-[3.25rem_1fr] border-b border-gray-50 last:border-b-0">
            <p className="px-1.5 py-2 text-right text-[10px] font-medium text-gray-400">{hourLabel(h)}</p>
            <DroppableHourRow day={day} hour={h}>
              <div className="flex flex-col gap-0.5">
                {(byHour.get(h) ?? []).map((entry) => (
                  <DraggableEntryChip key={`${entry.kind}_${entry.id}`} entry={entry} onOpen={openEntry} />
                ))}
              </div>
            </DroppableHourRow>
          </div>
        ))}
      </div>
    );
  };

  return (
    <MarketingPageShell
      title="Calendar"
      description="Unified schedule across Content, Social queue, and Press queue — reschedule from the calendar grid."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Studio' },
        { label: 'Calendar' },
      ]}
      icon={CalendarClock}
    >
      {/* An unreachable API and an empty schedule both render as a blank grid,
          and a blank grid is exactly what "nothing is scheduled" looks like. Say
          which it is before anyone concludes the queue is clear. */}
      {isError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm">
          <WifiOff className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">Could not load the schedule</p>
            <p className="mt-0.5 text-xs text-gray-600">
              {getApiErrorMessage(error, 'The marketing API did not respond')} — the grid below is
              empty because nothing loaded, not because nothing is scheduled.
            </p>
          </div>
        </div>
      )}
      {isLoading && (
        <div className="mb-4 rounded-lg border border-gray-100 bg-white px-3 py-2.5 text-sm text-gray-500">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading the schedule…
        </div>
      )}

      <div className="mb-5 space-y-3">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <InsightTile
            label="Upcoming"
            value={String(insights.upcoming)}
            meta={[`${insights.past} past`, `${entries.length} total`]}
            icon={Clock3}
            tone="bg-sky-50 text-sky-700"
            onClick={() => setWindowFilter('upcoming')}
          />
          <InsightTile
            label="Content"
            value={String(insights.byKind.content)}
            meta={['master pieces with a date']}
            icon={FileText}
            tone="bg-blue-50 text-blue-700"
            onClick={() => setKindFilter('content')}
          />
          <InsightTile
            label="Social queue"
            value={String(insights.byKind.social)}
            meta={['owned-account posts']}
            icon={Send}
            tone="bg-fuchsia-50 text-fuchsia-700"
            onClick={() => setKindFilter('social')}
          />
          <InsightTile
            label="Press queue"
            value={String(insights.byKind.traditional)}
            meta={[`${insights.linked} with Content linked`]}
            bar={{
              value: entries.length ? insights.linked / entries.length : 0,
              className: 'bg-violet-500',
            }}
            icon={CheckCircle2}
            tone="bg-violet-50 text-violet-700"
            onClick={() => setKindFilter('traditional')}
          />
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <ConnectionCallout
            title="How this connects"
            links={[
              { label: 'Content', to: '/admin/marketing/studio/content' },
              { label: 'Social queue', to: '/admin/marketing/studio/social' },
              { label: 'Press queue', to: '/admin/marketing/studio/traditional' },
              { label: 'Campaigns', to: '/admin/marketing/campaigns' },
            ]}
          >
            Calendar is a read/reschedule lens over items that already have a{' '}
            <span className="font-medium text-gray-800">scheduledAt</span>. Use Day / Week / Month / Agenda under the
            Calendar tab. Drag to reschedule; double-click a chip to open.
            {insights.next ? (
              <>
                {' '}
                Next up: <span className="font-medium text-gray-800">{insights.next.title}</span>.
              </>
            ) : null}
          </ConnectionCallout>
          <ConnectionCallout
            title="Not this page"
            links={[
              { label: 'Series cadence', to: '/admin/marketing/studio/series' },
              { label: 'Approvals', to: '/admin/marketing/crew/approvals' },
            ]}
          >
            Series cadence lives under Studio → Series. Approval gates are in Crew → Approvals.
          </ConnectionCallout>
        </div>
      </div>

      <MarketingFilterPanel
        className="mb-3"
        summary={marketingMatchSummary(filteredEntries.length, entries.length, 'item', isLoading)}
        showClear={filtersActive}
        onClear={clearFilters}
      >
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                tab === t ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {t}
            </button>
          ))}
        </div>
        <MarketingFilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search title, campaign, content…"
        />
        <MarketingFilterSelect
          options={kindFilterOptions}
          value={kindFilter}
          onChange={(value) => setKindFilter(value as ScheduleKind | 'all')}
          placeholder="All kinds"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <MarketingFilterSelect
          options={campaignFilterOptions}
          value={campaignFilter}
          onChange={setCampaignFilter}
          placeholder="All plays"
          clearValue="all"
          loading={plays.isLoading}
          className={`${marketingFilterSelectClass} sm:min-w-[12rem]`}
        />
        <MarketingFilterSelect
          options={windowFilterOptions}
          value={windowFilter}
          onChange={(value) => setWindowFilter(value as TimeWindow)}
          placeholder="Upcoming"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <MarketingFilterSelect
          options={sortOptions}
          value={sort}
          onChange={(value) => setSort(value as ScheduleSort)}
          placeholder="Sort"
          clearValue="soonest"
          className={marketingFilterSelectClass}
        />
      </MarketingFilterPanel>

      {tab === 'list' ? (
        filteredEntries.length === 0 ? (
          <EmptyState label="Nothing matches these filters." />
        ) : (
          <div className="space-y-1.5">
            {filteredEntries.map((entry) => {
              const Icon = KIND_ICON[entry.kind];
              return (
                <div
                  key={`${entry.kind}_${entry.id}`}
                  className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => openEntry(entry)}
                    className="flex min-w-0 flex-1 items-center gap-3 hover:opacity-90"
                  >
                    <div
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                        KIND_TONE[entry.kind],
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{entry.title}</p>
                      <p className="truncate text-[11px] text-gray-400">
                        {KIND_LABEL[entry.kind]} · {entry.meta}
                        {entry.playLabel ? ` · ${entry.playLabel}` : ''}
                      </p>
                    </div>
                    <p className="hidden shrink-0 text-xs text-gray-500 sm:block">
                      {new Date(entry.date).toLocaleString()}
                    </p>
                  </button>
                  <div className="flex shrink-0 flex-col items-end gap-0.5 text-[11px]">
                    <span className="text-gray-500 sm:hidden">{new Date(entry.date).toLocaleString()}</span>
                    {entry.playLabel ? (
                      <Link
                        to="/admin/marketing/campaigns"
                        className="font-medium text-blue-700 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {entry.playLabel} →
                      </Link>
                    ) : (
                      <span className="text-gray-400">No play linked</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
                {CAL_VIEWS.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setCalendarView(v.id)}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                      calendarView === v.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
                    )}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Previous"
                  onClick={() => shiftFocus(-1)}
                  className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 hover:bg-gray-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <p className="min-w-[11rem] text-center text-sm font-medium text-gray-800">{rangeLabel}</p>
                <button
                  type="button"
                  aria-label="Next"
                  onClick={() => shiftFocus(1)}
                  className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 hover:bg-gray-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setFocusDate(utcDateOnly(new Date()))}
                  className="ml-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
                >
                  Today
                </button>
              </div>
            </div>
            <p className="text-[11px] text-gray-400">
              {calendarView === 'agenda'
                ? 'Chronological for this month'
                : 'Drag to reschedule · double-click a chip to open'}
            </p>
          </div>

          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            {calendarView === 'day' && renderHourGrid(focusDayKey)}

            {calendarView === 'week' && (
              <div className="overflow-x-auto">
                <div className="mb-1 grid min-w-[56rem] grid-cols-7 gap-2">
                  {weekDays.map((d) => (
                    <button
                      key={d.toISOString()}
                      type="button"
                      onClick={() => {
                        setFocusDate(d);
                        setCalendarView('day');
                      }}
                      className={cn(
                        'rounded-md px-1 py-1 text-center text-[11px] font-medium',
                        d.toISOString().slice(0, 10) === focusDayKey
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-500 hover:bg-gray-100',
                      )}
                    >
                      {formatDayLabel(d, { weekday: 'short', month: 'short', day: 'numeric' })}
                    </button>
                  ))}
                </div>
                <div className="grid min-w-[56rem] grid-cols-7 gap-2">
                  {weekDays.map((d) => {
                    const key = d.toISOString().slice(0, 10);
                    return (
                      <DroppableDayCell key={key} day={key} className="min-h-[280px]">
                        <div className="space-y-1">
                          {(byDay.get(key) ?? []).map((entry) => (
                            <div key={`${entry.kind}_${entry.id}`}>
                              <p className="mb-0.5 text-[9px] text-gray-400">
                                {new Date(entry.date).toLocaleTimeString(undefined, {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  timeZone: 'UTC',
                                })}
                              </p>
                              <DraggableEntryChip entry={entry} onOpen={openEntry} dense />
                            </div>
                          ))}
                          {(byDay.get(key) ?? []).length === 0 && (
                            <p className="pt-8 text-center text-[10px] text-gray-300">—</p>
                          )}
                        </div>
                      </DroppableDayCell>
                    );
                  })}
                </div>
              </div>
            )}

            {calendarView === 'month' && (
              <>
                <div className="mb-1 grid grid-cols-7 gap-2">
                  {WEEKDAYS.map((w) => (
                    <p
                      key={w}
                      className="text-center text-[11px] font-medium uppercase tracking-wide text-gray-400"
                    >
                      {w}
                    </p>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {monthCells.map((day, i) =>
                    day === null ? (
                      <div key={`blank_${i}`} />
                    ) : (
                      <DroppableDayCell key={day} day={day} className="min-h-[92px]">
                        <button
                          type="button"
                          onClick={() => {
                            setFocusDate(new Date(`${day}T00:00:00Z`));
                            setCalendarView('day');
                          }}
                          className={cn(
                            'mb-1 text-[11px] font-medium',
                            day === focusDayKey ? 'text-blue-700' : 'text-gray-400',
                          )}
                        >
                          {new Date(`${day}T00:00:00Z`).getUTCDate()}
                        </button>
                        <div className="space-y-1">
                          {(byDay.get(day) ?? []).map((entry) => (
                            <DraggableEntryChip
                              key={`${entry.kind}_${entry.id}`}
                              entry={entry}
                              onOpen={openEntry}
                              dense
                            />
                          ))}
                        </div>
                      </DroppableDayCell>
                    ),
                  )}
                </div>
              </>
            )}

            {calendarView === 'agenda' &&
              (agendaEntries.length === 0 ? (
                <EmptyState label="Nothing scheduled in this month." />
              ) : (
                <div className="space-y-1.5">
                  {agendaEntries.map((entry) => {
                    const Icon = KIND_ICON[entry.kind];
                    return (
                      <button
                        key={`${entry.kind}_${entry.id}`}
                        type="button"
                        onClick={() => openEntry(entry)}
                        className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left hover:bg-gray-50"
                      >
                        <div
                          className={cn(
                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                            KIND_TONE[entry.kind],
                          )}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900">{entry.title}</p>
                          <p className="truncate text-[11px] text-gray-400">
                            {KIND_LABEL[entry.kind]} · {entry.meta}
                            {entry.playLabel ? ` · ${entry.playLabel}` : ''}
                          </p>
                        </div>
                        <p className="shrink-0 text-xs text-gray-500">
                          {new Date(entry.date).toLocaleString(undefined, { timeZone: 'UTC' })}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ))}
          </DndContext>
        </div>
      )}
    </MarketingPageShell>
  );
};

export default SchedulingPage;
